/**
 * Encryption of the offline data on the device (ADR-0013): AES-256-GCM with the device key from
 * `POST /api/sync/key`. The key is imported as non-extractable, so scripts can use it but never
 * read it out, and every value is bound to where it is stored (`context`), so a ciphertext moved
 * to another record does not open.
 */

export interface Sealed {
  iv: Uint8Array<ArrayBuffer>;
  data: ArrayBuffer;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function importDeviceKey(key: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromBase64Url(key), 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
}

/** Raw bytes, e.g. a file kept on the device. */
export async function sealBytes(
  key: CryptoKey,
  context: string,
  bytes: BufferSource,
): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(context) },
    key,
    bytes,
  );
  return { iv, data };
}

/** Throws when the key or the context does not match. */
export function unsealBytes(key: CryptoKey, context: string, sealed: Sealed): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: sealed.iv, additionalData: encoder.encode(context) },
    key,
    sealed.data,
  );
}

export function seal(key: CryptoKey, context: string, value: unknown): Promise<Sealed> {
  return sealBytes(key, context, encoder.encode(JSON.stringify(value)));
}

/** Throws when the key or the context does not match. */
export async function unseal<T>(key: CryptoKey, context: string, sealed: Sealed): Promise<T> {
  return JSON.parse(decoder.decode(await unsealBytes(key, context, sealed))) as T;
}
